import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  ColumnDef
} from '@tanstack/react-table';
import { StageFailureDetail, StageFailureSortBy } from '../../../const/StageFailureTypes';
import { useStageFailureDetails } from '../hooks/use-stage-failure-details';
import { useTheme } from '../../../hooks/useTheme';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { LOADER_DEBOUNCE_MS } from '../../../const/global-constants';
import { Pagination, Loading, Button } from '@dsi/react-eds';
import styles from './FailedSwDeliveryTable.module.css';

const FILTER_DEBOUNCE_MS = 500;

export interface FailedSwDeliveryTableProps {
  startDate: string;
  endDate: string;
  isMaximized?: boolean;
}

const columnToSortByMap: Record<string, StageFailureSortBy> = {
  workflowName: 'workflow_name',
  workflowId: 'workflow_id',
  stage: 'stage',
  stepName: 'step_name',
  timestamp: 'step_timestamp',
  errorSummary: 'error_summary'
};

function formatTimestamp(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function formatEnvironment(env: string[]): string {
  if (!Array.isArray(env) || env.length === 0) return '—';
  return env.join(', ');
}

function columnFiltersToApiFilters(filters: ColumnFiltersState): {
  stage?: string;
  workflowId?: string;
  workflowName?: string;
  stepName?: string;
  environment?: string;
  errorSummary?: string;
} {
  const out: Record<string, string> = {};
  filters.forEach((f) => {
    if (typeof f.value === 'string' && f.value.trim() !== '') {
      out[f.id] = f.value.trim();
    }
  });
  return out as {
    stage?: string;
    workflowId?: string;
    workflowName?: string;
    stepName?: string;
    environment?: string;
    errorSummary?: string;
  };
}

const FailedSwDeliveryTable: React.FC<FailedSwDeliveryTableProps> = ({ startDate, endDate, isMaximized = false }) => {
  const { theme } = useTheme();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'timestamp', desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [debouncedFilters, setDebouncedFilters] = useState<ColumnFiltersState>([]);
  const filterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sortBy: StageFailureSortBy =
    sorting.length > 0 && sorting[0].id && columnToSortByMap[sorting[0].id] ? columnToSortByMap[sorting[0].id] : 'step_timestamp';
  const sortOrder = sorting.length > 0 && sorting[0].desc ? 'DESC' : 'ASC';
  const apiFilters = useMemo(() => columnFiltersToApiFilters(debouncedFilters), [debouncedFilters]);

  const { data, total, isLoading, isFetching } = useStageFailureDetails({
    startDate,
    endDate,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sortBy,
    sortOrder,
    filters: apiFilters
  });

  const [debouncedFetching, setDebouncedFetching] = useState(false);
  useEffect(() => {
    if (isFetching) {
      setDebouncedFetching(true);
      return;
    }
    const timer = setTimeout(() => setDebouncedFetching(false), LOADER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isFetching]);

  const columns = useMemo<ColumnDef<StageFailureDetail>[]>(
    () => [
      {
        accessorKey: 'workflowName',
        id: 'workflowName',
        header: 'Workflow Name',
        cell: (info) => info.getValue() as string,
        enableColumnFilter: true
      },
      {
        accessorKey: 'workflowId',
        id: 'workflowId',
        header: 'Workflow ID',
        cell: (info) => {
          const workflowId = info.getValue() as string;
          const automationFlowId = info.row.original.automationFlowId;
          if (workflowId && automationFlowId) {
            return (
              <Link to={`/automation-flows/${workflowId}/${automationFlowId}`} className={styles.workflowIdLink}>
                {workflowId}
              </Link>
            );
          }
          return <span className={styles.workflowIdText}>{workflowId}</span>;
        },
        enableColumnFilter: true
      },
      {
        accessorKey: 'stage',
        id: 'stage',
        header: 'Failed Stage',
        cell: (info) => info.getValue() as string,
        enableColumnFilter: true
      },
      {
        accessorKey: 'stepName',
        id: 'stepName',
        header: 'Failed Step',
        cell: (info) => info.getValue() as string,
        enableColumnFilter: true
      },
      {
        accessorKey: 'timestamp',
        id: 'timestamp',
        header: 'Date',
        cell: (info) => formatTimestamp(info.getValue() as string),
        enableColumnFilter: false
      },
      {
        accessorKey: 'environment',
        id: 'environment',
        header: 'Environment',
        cell: (info) => formatEnvironment(info.getValue() as string[]),
        enableSorting: false,
        enableColumnFilter: true
      },
      {
        accessorKey: 'errorSummary',
        id: 'errorSummary',
        header: 'Error Summary',
        cell: (info) => info.getValue() as string,
        enableColumnFilter: true
      }
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: { pagination, sorting, columnFilters },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
      filterTimeoutRef.current = setTimeout(() => {
        setDebouncedFilters(typeof updater === 'function' ? updater(columnFilters) : updater);
      }, FILTER_DEBOUNCE_MS);
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: Math.ceil(total / pagination.pageSize) || 1,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
    enableMultiSort: false
  });

  const handleClearFilters = () => {
    setColumnFilters([]);
    setDebouncedFilters([]);
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
      filterTimeoutRef.current = null;
    }
    table.setPageIndex(0);
  };

  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    };
  }, []);

  const pag = table.getState().pagination;
  const from = total === 0 ? 0 : pag.pageIndex * pag.pageSize + 1;
  const to = Math.min((pag.pageIndex + 1) * pag.pageSize, total);
  const hasNoData = !isLoading && data.length === 0;
  const hasActiveFilters = debouncedFilters.some((f) => typeof f.value === 'string' && f.value.trim() !== '');

  if (hasNoData && !hasActiveFilters && !isFetching) {
    return (
      <div className={styles.noDataContainer}>
        <i className={`icon icon-failed ${styles.dangerIcon}`} />
        <div className={styles.noDataTitle}>Data Unavailable</div>
        <div className={styles.noDataSubtitle}>No failed stages data available for the selected time range</div>
      </div>
    );
  }

  const rootClassName = isMaximized ? `${styles.root} ${styles.rootMaximized}` : styles.root;

  const tableElement = (
    <table className={styles.table}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className={styles.headerRow}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className={styles.headerCell} style={{ textAlign: 'left', padding: '12px 16px' }}>
                <div
                  className={header.column.getCanSort() ? styles.sortableHeader : styles.unsortableHeader}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <span className={styles.sortIcon}>
                      {header.column.getIsSorted() === 'asc' ? (
                        <i className="icon icon-sort-up" style={{ transform: 'rotate(180deg)' }} />
                      ) : header.column.getIsSorted() === 'desc' ? (
                        <i className="icon icon-sort-down" />
                      ) : (
                        <i className="icon icon-sorting" />
                      )}
                    </span>
                  )}
                </div>
                {header.column.getCanFilter() && showFilters ? (
                  <div className={styles.filterContainer}>
                    <input
                      type="text"
                      value={(header.column.getFilterValue() ?? '') as string}
                      onChange={(e) => {
                        header.column.setFilterValue(e.target.value);
                        table.setPageIndex(0);
                      }}
                      placeholder={header.column.id === 'environment' ? 'e.g. lab,prod' : 'Filter'}
                      className={styles.filterInput}
                    />
                  </div>
                ) : null}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {isLoading || debouncedFetching ? (
          <tr><td colSpan={columns.length} style={{ padding: 0, height: 0, border: 'none' }} /></tr>
        ) : table.getRowModel().rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
              No matching results found. Try adjusting your filters.
            </td>
          </tr>
        ) : (
          table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={styles.tableRow}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '';
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={styles.tableCell}
                  style={{
                    borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className={rootClassName}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.itemCount}>{isLoading ? 'Loading...' : total > 0 ? `${from}-${to} of ${total} items` : 'No items'}</span>
        </div>
        <div className={styles.buttonGroup}>
          <ReactEDSWrapper component={Button} icon={showFilters ? 'filter-off' : 'filter'} onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Close Filters' : 'Filters'}
          </ReactEDSWrapper>
          {showFilters && (
            <ReactEDSWrapper component={Button} icon="filter-clear" onClick={handleClearFilters}>
              Clear Filters
            </ReactEDSWrapper>
          )}
        </div>
      </div>
      {isMaximized ? <div className={styles.tableScrollContainer}>{tableElement}</div> : <div className={styles.tableWrapper}>{tableElement}</div>}
      {(isLoading || debouncedFetching) && (
        <div className={styles.loadingContainer}>
          <ReactEDSWrapper component={Loading} size="large" />
        </div>
      )}
      <ReactEDSWrapper
        component={Pagination}
        numPages={Math.ceil(total / pagination.pageSize) || 1}
        selectedPage={table.getState().pagination.pageIndex + 1}
        onPaginationChangePage={(page: number) => table.setPageIndex(page - 1)}
        optionsList={[
          { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
          { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
          { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
          { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
          { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
        ]}
        onPaginationChangeSelect={(size: string) => {
          table.setPageSize(Number(size));
          table.setPageIndex(0);
        }}
        withActions
      />
    </div>
  );
};

export default FailedSwDeliveryTable;
